import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionActions from "@mui/material/AccordionActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { cloneDeep } from "lodash";
import { licenseSchema } from "components/Appraiser/AppraiserProfileEdit";
import { getAllLicenses, addAppraiserLicense, updateAppraiserLicense, deleteAppraiserLicenseImage, deleteAppraiserLicense } from "requests/appraiser";
import { MDropzone, MCard } from "components/Shared";
import FilePreview from "containers/Homeowner/MCaseView/FileManager/FilePreview";
import WebConfig from "middlewares/WebConfig";
import { connect } from "react-redux";
import TextBlock from "components/Shared/TextBlock";
import { APPRAISER_LICENSE_VIEW, APPRAISER_LICENSE_EDIT, APPRAISER_LICENSE_DELETE, APPRAISER_LICENSE_ADD } from "typeConstants/rights";

const statusList = [
  { name: "Valid", id: "VALID" },
  { name: "Expired", id: "EXPIRED" },
  { name: "Invalid", id: "INVALID" },
  { name: "Remove", id: "REMOVE" }];

const RenderLicenses = props => { // extends React.Component {
  const [licenses, setlicenses] = useState([]);
  const [expand, setexpand] = useState([]);
  const [dirty, setDirty] = useState(-1);
  const [previewFile, setPreviewFile] = useState(null);
  const [showDeleteLicenseDialog, setShowDeleteLicenseDialog] = useState(false);
  const [deletePaused, setDeletePaused] = useState(false);
  const [listenerAttached, setListenerAttached] = useState(false);

  let mounted = useRef(true);

  // The Appraiser list will preventDefault and cause issues when trying to upload a file so we need to stop propagation
  const mouseEventHandler = (x) => {
    x.stopPropagation();
  };

  const viewRight = props.rights.includes(APPRAISER_LICENSE_VIEW);
  const editRights = props.rights.includes(APPRAISER_LICENSE_EDIT);
  const addRights = props.rights.includes(APPRAISER_LICENSE_ADD);
  const deleteRights = props.rights.includes(APPRAISER_LICENSE_DELETE);

  const dropzoneRightsOverrides = {
    download: viewRight,
    preview: viewRight,
    delete: editRights,
  };

  useEffect(
    () => {
      if (editRights) {
        document.addEventListener("dragover", mouseEventHandler);
        setListenerAttached(true);
      }
      return () => {
        mounted.current = false;
        if (listenerAttached) document.removeEventListener("dragover", mouseEventHandler);
      };
    }, []);

  useEffect(() => {
    reload();
  }, [props.appraiserNumber]);

  const reload = () => {
    getAllLicenses(props.appraiserNumber, sortLicenses, mounted);
    setDirty(-1);
  };

  const sortLicenses = (licenses) => {
    licenses.sort((a, b) => b.expirationDate.localeCompare(a.expirationDate) ?? a.licenseState.name.localeCompare(b.licenseState.name));
    setlicenses(licenses);
  };

  const loadFiles = (data) => {
    if (!data || !data.licenseImageUrl) return [];
    const { webApiRoot } = WebConfig;
    return [{
      "canDelete": true,
      "downloadUrl": `${webApiRoot}/appraiserlicenses/${data.id}/image/download`,
      "fileName": "License file.jpg",
      "viewUrl": `${webApiRoot}/appraiserLicenses/${data.id}/image/view`
    }];
  };

  const blankLicense = () => {
    return {
      id: 0,
      appraiserNumber: props.appraiserNumber,
      licenseNumber: "",
      licenseCertificateType: { id: 0, name: "", code: "", description: "", appraisalSubcommitteeName: "" },
      licenseState: { id: 0, abbreviation: "", name: "", abbreviationAndName: "" },
      effectiveDate: "",
      expirationDate: "",
      licenseStatus: "VALID",
      hasLicenseImage: false,
      licenseImageUrl: "",
    };
  };

  const copyLicense = (data) => {
    let copiedLicense = blankLicense();
    copiedLicense.licenseCertificateType = cloneDeep(data.licenseCertificateType);
    copiedLicense.licenseNumber = data.licenseNumber;
    copiedLicense.licenseState = cloneDeep(data.licenseState);
    copiedLicense.licenseStatus = "VALID";
    return copiedLicense;
  };

  const primaryLabel = (license) => {
    if (license.id == 0) {
      // must be a new item
      return "New License";
    }
    return (license.licenseCertificateType.name);
  };
  const secondaryLabel = (license) => {
    if (license.id == 0) {
      // must be a new item
      return "";
    }
    return ((license.licenseStatus === "VALID" ? "" : license.licenseStatus + ":  ") + license.licenseNumber + " in " + license.licenseState.name);
  };

  const addAttachments = (files, data) => {
    if (!editRights) return;
    const filteredFiles = files.filter(({ file }) => file);
    if (filteredFiles.length !== 0)
      filteredFiles.map(({ file }) => {

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = function (evt) {
          if (evt.target.readyState == FileReader.DONE) {
            let convertedfile = evt.target.result;
            let cleanfile = convertedfile.substring(convertedfile.lastIndexOf(",") + 1);
            data.licenseImage = cleanfile;
            saveLicense(data);
          }
          reader.onerror = function () {
            window.console.log("Error: Unable to load image");
          };
        };
      });

  };

  const saveLicense = (data) => {
    const successSave = () => {
      reload();
    };

    //standard request object when updating or adding an appraiser
    const getDTO = (data) => {
      return {
        "AppraiserNumber": props.appraiserNumber,
        "StateAbbreviation": data.licenseState.abbreviation,
        "Number": data.licenseNumber,
        "AppraiserLicenseCertificateTypeCode": data.licenseCertificateType.code,
        "EffectiveDate": data.effectiveDate,
        "ExpirationDate": data.expirationDate,
        "Status": data.licenseStatus,
        "LicenseImage": data.licenseImage
      };
    };

    const addAppraiser = (data) => {
      addAppraiserLicense(getDTO(data), successSave, mounted);
    };

    const updateAppraiser = (data) => {
      let requestObject = getDTO(data);
      requestObject["Id"] = data.id;
      updateAppraiserLicense(requestObject, successSave, mounted);
    };

    (data.id == 0) ? addAppraiser(data) : updateAppraiser(data);

  };

  const deleteAttachments = (data) => {
    const successDelete = () => {
      reload();
    };

    if (!editRights) return;
    deleteAppraiserLicenseImage(data.id, successDelete, mounted);
  };

  const deleteLicense = (data) => {
    if (!deleteRights) return;
    const successCallback = () => {
      setDeletePaused(false);
      reload();
    };
    const errorCallback = () => {
      setDeletePaused(false);
      reload();
    };
    setDeletePaused(true);
    deleteAppraiserLicense(data.id, successCallback, errorCallback, mounted);
  };

  const deleteActions = (currentItem, index) => deletePaused ? [<Button
    className="m-t-10"
    key="yes"
    disabled
  >
    deleting
  </Button>] : [
      <Button
        className="m-t-10"
        key="yes"
        onClick={() => {
          deleteLicense(currentItem);
          let newLicenses = cloneDeep(licenses);
          newLicenses.splice(index, 1);
          setlicenses(newLicenses);
          let newExpand = [];
          setexpand(newExpand);
          setShowDeleteLicenseDialog(false);
        }}
      >
        Yes
    </Button>,
      <Button
        className="m-t-10"
        key="nah"
        onClick={() => {
          setShowDeleteLicenseDialog(false);
        }}
      >
        No
    </Button>
    ];

  const openFilePreview = (file, urlWithSSO) => {
    let tab = window.open(urlWithSSO, "_blank");
    tab.focus();
  };

  let cardActions = previewFile ? [{ icon: "close", helpText: "Close Preview", onClick: () => setPreviewFile(null) }] : [];

  return (viewRight &&
    <div className="md-grid">
      <div className="md-cell md-cell--12 md-cell--right">
        {licenses && licenses.map((license, index) => {
          let currentItem = license;
          return (<Accordion key={index}
            expanded={expand[index] ? expand[index] : false}
            onChange={(event, isExpanded) => {
              let newexpand = cloneDeep(expand);
              newexpand[index] = isExpanded;
              setexpand(newexpand);
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{`${primaryLabel(currentItem)}`}</Typography>
              <Typography sx={{ color: 'text.secondary', ml: 1 }}>{`${secondaryLabel(currentItem)}`}</Typography>
            </AccordionSummary>
            <AccordionDetails className="md-grid">
              {(dirty == -1 && deleteRights) &&
                <Button
                  className="m-t-10 save"
                  disabled={dirty != -1 || !editRights}
                  onClick={() => {
                    setShowDeleteLicenseDialog(true);
                  }}>Delete License
              </Button>
              }
              <Dialog
                open={showDeleteLicenseDialog}
                onClose={() => setShowDeleteLicenseDialog(false)}
                sx={{ '& .MuiDialog-paper': { width: 500 } }}
              >
                <DialogTitle>Delete License</DialogTitle>
                <DialogContent>
                  <TextBlock field="Are you sure?" />
                </DialogContent>
                <DialogActions>
                  {deleteActions(currentItem, index)}
                </DialogActions>
              </Dialog>

              {(dirty == -1 && addRights) &&
                <Button
                  className="m-t-10 save"
                  disabled={dirty != -1 || !editRights}
                  onClick={() => {
                    let newLicenses = cloneDeep(licenses);
                    newLicenses.push(copyLicense(currentItem));
                    setlicenses(newLicenses);
                    let newExpand = [];
                    newExpand[newLicenses.length - 1] = true;
                    setexpand(newExpand);
                    setDirty(newLicenses.length - 1);
                  }}>Copy License
              </Button>
              }
              {(dirty != -1 && dirty != index) && <div style={{ color: "red" }}> Unable to edit license with unsaved changes</div>}


              <TextField
                variant="standard"
                id="text-field-1"
                className="md-cell md-cell--12"
                label="License #"
                required
                disabled={dirty != -1 && dirty != index || !editRights}
                onChange={(e) => {
                  let newLicenses = cloneDeep(licenses);
                  newLicenses[index].licenseNumber = e.target.value;
                  setDirty(index);
                  setlicenses(newLicenses);
                }}
                value={licenses[index].licenseNumber ? licenses[index].licenseNumber : ""}
              />
              <TextField
                select
                variant="standard"
                id="select-field-1"
                className="md-cell md-cell--8"
                label="License Type"
                required
                disabled={dirty != -1 && dirty != index || !editRights}
                onChange={(e) => {
                  let newLicenses = cloneDeep(licenses);
                  let selectedCertificate = props.certificateList.filter(x => { return x.id == e.target.value; });

                  newLicenses[index].licenseCertificateType = selectedCertificate[0];
                  setDirty(index);
                  setlicenses(newLicenses);
                }}
                value={licenses[index].licenseCertificateType.id}
              >
                {props.certificateList.map(x => (
                  <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="standard"
                id="select-field-2"
                className="md-cell md-cell--4 md-cell--right"
                required
                disabled={dirty != -1 && dirty != index || !editRights}
                label="State"
                onChange={(e) => {
                  let newLicenses = cloneDeep(licenses);
                  let selectedState = props.stateList.filter(x => { return x.stateAbbreviation == e.target.value; });
                  newLicenses[index].licenseState = { id: selectedState[0].id, abbreviation: selectedState[0].stateAbbreviation, name: selectedState[0].stateName };
                  setDirty(index);
                  setlicenses(newLicenses);
                }}
                value={licenses[index].licenseState.abbreviation}
              >
                {props.stateList.map(x => (
                  <MenuItem key={x.stateAbbreviation} value={x.stateAbbreviation}>{x.stateName}</MenuItem>
                ))}
              </TextField>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Effective Date"
                value={licenses[index].effectiveDate ? new Date(licenses[index].effectiveDate) : null}
                disabled={dirty != -1 && dirty != index || !editRights}
                onChange={(newValue) => {
                  let newLicenses = cloneDeep(licenses);
                  newLicenses[index].effectiveDate = newValue;
                  setDirty(index);
                  setlicenses(newLicenses);
                }}
                slotProps={{ textField: { variant: 'standard', required: true, className: 'md-cell md-cell--6' } }}
              />
              <DatePicker
                label="Expiration Date"
                value={licenses[index].expirationDate ? new Date(licenses[index].expirationDate) : null}
                disabled={dirty != -1 && dirty != index || !editRights}
                onChange={(newValue) => {
                  let newLicenses = cloneDeep(licenses);
                  newLicenses[index].expirationDate = newValue;
                  setDirty(index);
                  setlicenses(newLicenses);
                }}
                slotProps={{ textField: { variant: 'standard', required: true, className: 'md-cell md-cell--6 md-cell--right' } }}
              />
            </LocalizationProvider>
              <TextField
                select
                variant="standard"
                id="select-field-3"
                className="md-cell md-cell--6 md-cell--right"
                required
                disabled={dirty != -1 && dirty != index || !editRights}
                label="Status"
                onChange={(e) => {
                  let newLicenses = cloneDeep(licenses);
                  newLicenses[index].licenseStatus = e.target.value;
                  setDirty(index);
                  setlicenses(newLicenses);
                }}
                value={licenses[index].licenseStatus}
              >
                {statusList.map(x => (
                  <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>
                ))}
              </TextField>
            {licenseSchema.isValidSync(license) &&
              <div className="manual-order-dropzone file-manager">
                <MCard
                  title="License File Manager"
                  className="full-height-card file-manager"
                  actions={cardActions}>

                  {previewFile ?
                    <FilePreview
                      displayname={previewFile.name}
                      path={previewFile.url}
                    /> :
                    <MDropzone
                      existingFiles={loadFiles(currentItem)}
                      multiple
                      noClick
                      onPreview={(file, urlWithSSO) => openFilePreview ? openFilePreview(file, urlWithSSO) : setPreviewFile({ name: file.name, url: urlWithSSO })}
                      onFileAdd={(file) => addAttachments(file, currentItem)}
                      onFileRemove={() => deleteAttachments(currentItem)}
                      prompt={editRights ? "Drop License File here" : "No License File attached"}
                      fileRightOverrides={dropzoneRightsOverrides}
                      columnDefinitionOverride={[
                        { name: "Name", dataSource: "name", className: "file-name-cell" },
                        { name: "Type", dataSource: "extension", style: { width: "50px" } },
                        { name: "Actions", dataSource: "actions", style: { width: "160px" } },
                      ]}
                    />
                  } </MCard>
              </div>
            }
            </AccordionDetails>
            <AccordionActions>
              <Button variant="text" disabled={!licenseSchema.isValidSync(license) || (dirty != index) || !editRights} onClick={() => saveLicense(currentItem)}>Save</Button>
              <Button variant="text" disabled={!editRights} onClick={() => {
                if (!currentItem.id) {
                  let newLicenses = cloneDeep(licenses);
                  newLicenses.splice(index);
                  setlicenses(newLicenses);
                }
                setDirty(-1);
                let newExpand = [];
                setexpand(newExpand);
              }}>Cancel</Button>
            </AccordionActions>
          </Accordion>
          );
        })}
      </div>
      {(dirty == -1 && addRights) && <Button className="md-cell md-cell--12 md-cell--right"
        variant="text"
        color="secondary"
        startIcon={<Icon>add</Icon>}
        disabled={dirty != -1}
        onClick={() => {
          let newLicenses = cloneDeep(licenses);
          newLicenses.push(blankLicense());
          setlicenses(newLicenses);
          let newExpand = [];
          newExpand[newLicenses.length - 1] = true;
          setexpand(newExpand);
          setDirty(newLicenses.length - 1);
        }}>Add License
          </Button>}
    </div>
  );
};

RenderLicenses.propTypes = {
  appraiserNumber: PropTypes.number,
  certificateList: PropTypes.array,
  stateList: PropTypes.array,
  roles: PropTypes.array,
  rights: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    roles: state.user.profile.roles,
    rights: state.user.profile.rights,
    certificateList: state.appraiser.certificateList,
    stateList: state.appraiser.stateList,
  };
};

export default connect(mapStateToProps)(RenderLicenses);
